/**
 * 흐르는 글자 읽기(통로 ① 화면 읽기 — L01 명찰 · L04 전광판) · 내 그림 전시(L02 픽셀 갤러리).
 *
 * 시뮬레이터의 `show string` 은 화면을 한 칸씩 왼쪽으로 밀고 오른쪽 끝에 새 세로 줄을 채운다(pxt-microbit `scrollImage` · 기본 150ms).
 * 확장은 0.1초마다 화면을 읽으므로 밀린 칸을 하나도 놓치지 않는다. 그래서 **'바로 앞 화면을 한 칸 민 것'** 이면 새 줄을 띠에 붙이고,
 * 화면이 다 비면(글자가 다 지나감) 띠 하나를 끝낸다. 끝난 띠는 글꼴(lib/font)로 글자를 읽어 본다 — 못 읽으면 띠 그림 그대로.
 *
 * 메시지마다 한 번씩 부른다(놀이 시계 0.1초에 맞추면 칸이 빠질 수 있다). 같은 화면(1초 살아 있음 알림)은 건너뛴다.
 */
import type { MbFrame } from "./live";
import { iconOf, isBlank } from "./live";
import { bitsOfLeds, columnOf, decodeColumns, glyphOf } from "./font";

/**
 * 흘러 지나간 낱말 하나. 화면이 다 비는 곳(띄어쓰기 · 문구 끝)에서 끊는다 — 빈 화면이 이어지는 동안에는 바뀐 게 없어 소식이 없으므로
 * 빈칸이 몇 줄이었는지 셀 수 없다(띄어쓰기 7줄 · 문구 끝 6줄을 가를 수 없다). 그래서 문구를 낱말로 받고 되풀이는 낱말로 센다.
 */
export interface Word {
  /** 세로 줄들(앞뒤 빈 줄 뺌) */
  readonly cols: readonly string[];
  /** 글꼴로 읽은 글자(못 읽으면 null — 흐르기 시작한 뒤에 연결됐거나 그림이 흐른 것) */
  readonly text: string | null;
}

export interface Scroll {
  /** 바로 앞 화면(켜짐 비트열) */
  readonly prev: string | null;
  /** 지금 흐르는 낱말(밀려 들어온 세로 줄들) */
  readonly cols: readonly string[];
  /** 마지막 변화가 '한 칸 밀기'였는가 */
  readonly shifting: boolean;
  /** 지나간 낱말(최근 16개) */
  readonly words: readonly Word[];
}

export const initialScroll = (): Scroll => ({ prev: null, cols: [], shifting: false, words: [] });

const BLANK_COL = "00000";
const BLANK = "0".repeat(25);
/** 낱말로 칠 최소 길이(세로 줄) — 글자 하나는 흐르지 않고 선다(`show string` 한 글자). 흐르는 중 낱말 끝의 짧은 조각도 버린다 */
export const WORD_MIN_COLS = 3;

const isShiftOf = (prev: string, bits: string): boolean => {
  for (let y = 0; y < 5; y++) if (prev.slice(y * 5 + 1, y * 5 + 5) !== bits.slice(y * 5, y * 5 + 4)) return false;
  return true;
};

const trim = (cols: readonly string[]): readonly string[] => {
  let a = 0, b = cols.length;
  while (a < b && cols[a] === BLANK_COL) a++;
  while (b > a && cols[b - 1] === BLANK_COL) b--;
  return cols.slice(a, b);
};

function finish(s: Scroll, cols: readonly string[]): readonly Word[] {
  const t = trim(cols);
  if (t.length < WORD_MIN_COLS) return s.words;
  return [...s.words, { cols: t, text: decodeColumns(t) }].slice(-16);
}

export function stepScroll(s: Scroll, frame: MbFrame | null): Scroll {
  if (!frame) return s;
  const bits = bitsOfLeds(frame.leds);
  if (bits === s.prev) return s;
  // 처음 받은 화면은 빈 화면 다음으로 본다(보드는 빈 화면으로 시작한다)
  if (isShiftOf(s.prev ?? BLANK, bits)) {
    const cols = [...s.cols, columnOf(bits, 4)].slice(-200);
    // 화면이 다 비었다 — 낱말 하나 끝(띄어쓰기거나 문구 끝)
    if (bits === BLANK) return { prev: bits, cols: [], shifting: true, words: finish(s, cols) };
    return { prev: bits, cols, shifting: true, words: s.words };
  }
  // 밀기가 아닌 변화 — 흐르던 낱말이 있었으면 거기서 끝(중간에 아이콘으로 바뀜). 흐르기 시작한 첫 줄은 새 낱말의 첫 줄
  return { prev: bits, cols: [], shifting: false, words: s.shifting ? finish(s, s.cols) : s.words };
}

/** 읽은 낱말들(못 읽은 것은 뺀다) */
export const readWords = (s: Scroll): string[] => s.words.flatMap((w) => (w.text ? [w.text] : []));

// ── 명찰 광장(L01) — 흐르는 이름 + 기분 아이콘 ───────────────────────

/** 이름으로 칠 낱말 — 읽었으면 글자 두 개 이상 · 못 읽었으면 세로 줄 11개 이상(글자 두 칸 가까이 — 그림 띠 그대로 명찰에) */
export const isName = (w: Word): boolean => (w.text ? w.text.length >= 2 : w.cols.length >= 11);
export const nameOf = (s: Scroll): Word | null => [...s.words].reverse().find(isName) ?? null;

// ── 학교 정문 전광판(L04) — 같은 문구가 두 번 이상 흐르기 ────────────

export const MARQUEE_REPEATS = 2;
/** 가장 많이 되풀이된 낱말(두 글자 이상)과 그 횟수 */
export function repeatsOf(s: Scroll): { word: string | null; n: number } {
  const count = new Map<string, number>();
  let best: { word: string | null; n: number } = { word: null, n: 0 };
  for (const w of readWords(s)) {
    if (w.length < 2) continue;
    const n = (count.get(w) ?? 0) + 1;
    count.set(w, n);
    if (n >= best.n) best = { word: w, n };
  }
  return best;
}

// ── 픽셀 갤러리(L02) — 아이콘 · 글자가 아닌 내 그림 ──────────────────

/** 이만큼 그대로 서 있어야 액자에 건다(흐르는 글자 · 애니메이션 한 장은 지나간다) */
export const GALLERY_HOLD_MS = 1000;
export const GALLERY_MAX = 3;

export interface Gallery {
  readonly cand: string | null;
  readonly held: number;
  /** 건 그림(켜짐 비트열 · 최대 3점) */
  readonly pieces: readonly string[];
}

export const initialGallery = (): Gallery => ({ cand: null, held: 0, pieces: [] });

/** 내 그림인가 — 비어 있지 않고 · 아이콘 40개도 · 글자 한 자도 아니다 */
export const isMyPicture = (leds: readonly number[]): boolean => !isBlank(leds) && iconOf(leds) === null && glyphOf(leds) === null;

export function stepGallery(s: Gallery, dtMs: number, frame: MbFrame | null): Gallery {
  if (!frame || !isMyPicture(frame.leds)) return s.cand === null ? s : { ...s, cand: null, held: 0 };
  const bits = bitsOfLeds(frame.leds);
  if (bits !== s.cand) return { ...s, cand: bits, held: 0 };
  const held = s.held + dtMs;
  const hang = held >= GALLERY_HOLD_MS && !s.pieces.includes(bits) && s.pieces.length < GALLERY_MAX;
  return { cand: bits, held, pieces: hang ? [...s.pieces, bits] : s.pieces };
}
